<template>
  <div class="container-fluid">
    <div class="bg-blue-50 px-10 py-8 mx-auto text-center">
      <p class="text-2xl md:text-3xl font-bold mb-2">Production Staff Resources</p>
      <p class="text-lg max-w-3xl mx-auto mb-2">
        Here at Atlas, we're focused on providing our users with the latest resources on topics that matter -
        inclusivity, diversity, sustainability, and the business of theatre! We strive to continually improve and grow
        our resource respository to best serve the Chicago theatre community.
      </p>
    </div>
    <nav class="flex flex-wrap justify-between text-base pb-0" aria-label="Breadcrumb">
      <ol role="list" class="items-center space-x-4 hidden sm:flex">
        <li>
          <div>
            <a href="/" class="text-gray-400 hover:text-gray-500">
              <HomeIcon class="h-5 w-5 flex-shrink-0" aria-hidden="true" />
              <span class="sr-only">Home</span>
            </a>
          </div>
        </li>
        <li v-for="page in pages" :key="page.name">
          <div class="flex items-center">
            <ChevronRightIcon class="h-5 w-5 flex-shrink-0 text-gray-400" aria-hidden="true" />
            <a
              :href="page.href"
              class="ml-4 font-medium text-gray-500 hover:text-gray-700"
              :aria-current="page.current ? 'page' : undefined"
              :class="[page.current ? 'underline' : 'no-underline']"
            >
              {{ page.name }}
            </a>
          </div>
        </li>
      </ol>
      <div class="flex flex-wrap px-6">
        <Menu as="div" class="relative inline-block text-left">
          <div>
            <MenuButton
              class="inline-flex w-full justify-center gap-x-1.5 rounded-md bg-white px-3 py-2 text-base font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
            >
              Quick Links
              <ChevronDownIcon class="-mr-1 h-5 w-5 text-gray-400" aria-hidden="true" />
            </MenuButton>
          </div>

          <transition
            enter-active-class="transition ease-out duration-100"
            enter-from-class="transform opacity-0 scale-95"
            enter-to-class="transform opacity-100 scale-100"
            leave-active-class="transition ease-in duration-75"
            leave-from-class="transform opacity-100 scale-100"
            leave-to-class="transform opacity-0 scale-95"
          >
            <MenuItems
              class="absolute z-10 mt-2 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
            >
              <div class="py-1">
                <MenuItem v-for="(value, name, index) in resources" :key="index">
                  <a
                    :href="'#' + name.replace(/\s/g, '')"
                    class="text-gray-700 block px-4 py-2 text-base hover:no-underline no-underline hover:bg-gray-100"
                  >
                    {{ name }}
                  </a>
                </MenuItem>
              </div>
            </MenuItems>
          </transition>
        </Menu>
      </div>
    </nav>
    <div class="container pb-5 px-5 m-auto">
      <div
        class="mx-auto max-w-7xl py-4 px-6 lg:px-8 border-b-2 last:border-b-0"
        v-for="(value, name, index) in resources"
        :key="index"
      >
        <div class="mx-auto max-w-2xl text-center mt-3">
          <h2 class="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl" :id="name.replace(/\s/g, '')">
            {{ name }}
          </h2>
          <p class="mt-2 text-lg leading-8 text-gray-600">
            A curated list of resources supporting the {{ name }} experience
          </p>
        </div>
        <div class="mx-auto mt-16 grid max-w-2xl grid-cols-1 gap-x-8 gap-y-20 lg:mx-0 lg:max-w-none lg:grid-cols-3">
          <article
            v-for="post in value"
            :key="post.id"
            class="group flex flex-col items-start justify-between relative hover:z-10"
          >
            <div class="relative w-full">
              <img
                :src="post.logo"
                alt=""
                class="aspect-[16/9] w-full rounded-xl bg-gray-100 object-cover sm:aspect-[2/1] lg:aspect-[3/2]"
              />
              <div class="absolute inset-0 rounded-xl ring-1 ring-inset ring-gray-900/10"></div>
            </div>
            <div class="max-w-xl relative">
              <div class="relative px-2">
                <h3 class="mt-4 text-lg font-semibold leading-6 text-gray-900 group-hover:text-gray-600">
                  <a
                    :href="post.website_url"
                    v-if="post.website_url"
                    class="text-blue-900 hover:text-blue-700 no-underline hover:no-underline"
                    target="_blank"
                  >
                    {{ post.organization_name ? post.organization_name : post.document_name }}
                  </a>
                  <a
                    v-else
                    :href="post.pdf_url"
                    class="text-blue-900 hover:text-blue-700 no-underline hover:no-underline"
                    target="_blank"
                  >
                    {{ post.organization_name ? post.organization_name : post.document_name }}
                  </a>
                </h3>
                <div class="info-container relative min-h-[80px]">
                  <p class="mt-4 line-clamp-3 text-base leading-6 text-gray-600">{{ post.description }}</p>
                  <div
                    class="extrainfo hidden absolute left-[-8px] right-[-8px] bottom-0 top-[-300px] p-4 bg-black bg-opacity-80 text-white rounded-xl group-hover:flex items-center justify-center cursor-pointer hover:bg-opacity-90 transition-all duration-300 z-50"
                    @click="openResource(post)"
                  >
                    <p class="text-base leading-relaxed text-center">{{ post.description }}</p>
                  </div>
                </div>
              </div>
            </div>
          </article>
        </div>
      </div>
    </div>

    <!-- Scroll to Top Button -->
    <button
      v-show="showScrollButton"
      @click="scrollToTop"
      class="fixed bottom-6 right-6 bg-blue-600 hover:bg-blue-700 text-white p-3 rounded-full shadow-lg transition-all duration-300 ease-in-out transform hover:scale-110 z-50"
      aria-label="Scroll to top"
    >
      <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 10l7-7m0 0l7 7m-7-7v18"></path>
      </svg>
    </button>
  </div>
</template>
<script>
import axios from "axios";
import { ChevronRightIcon, ChevronDownIcon, HomeIcon } from "@heroicons/vue/20/solid";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/vue";
export default {
  components: { ChevronRightIcon, ChevronDownIcon, HomeIcon, Menu, MenuButton, MenuItem, MenuItems },
  data: function () {
    return {
      resources: [],
      showScrollButton: false,
      pages: [
        { name: "Resources", href: "/resources", current: false },
        { name: "Production Staff Resources", href: "/resources/production", current: true },
      ],
    };
  },
  mounted() {
    this.getResources();
    window.addEventListener("scroll", this.handleScroll);
  },
  beforeUnmount() {
    window.removeEventListener("scroll", this.handleScroll);
  },
  methods: {
    getResources() {
      axios.get("/resources.json?primary_filter=Production%20Staff%20Resources").then((response) => {
        this.resources = response.data.grouped["Production Staff Resources"];
      });
    },
    handleScroll() {
      this.showScrollButton = window.pageYOffset > 300;
    },
    scrollToTop() {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    },
    openResource(post) {
      const url = post.website_url || post.pdf_url;
      if (url) {
        window.open(url, '_blank');
      }
    },
  },
};
</script>
<style></style>
